"use client";

/**
 * React Query bindings for the Media Library (Studio content engine).
 *
 * The artifact store is GET /api/media/artifacts — the user's own ready media
 * across every task, each URL re-signed server-side. There is NO NEXT_PUBLIC
 * flag: the web's gating channel is data/endpoint presence. A 404 means the
 * server flag (CONTENT_ENGINE_V1) is off, so `available` is false and the
 * Knowledge layout hides the Media tab; a 200 (even with an empty list) means
 * the feature is on, so the tab shows and the grid renders its empty state.
 */

import { useQuery } from "@tanstack/react-query";
import type { MediaArtifact, MediaArtifactsResponse } from "@bash/shared";
import { useAuth } from "@/contexts/AuthContext";
import { resilientFetch } from "@/lib/fetch";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

interface ArtifactsResult {
  /** true when the endpoint responded 200 (feature on); false on 404 (off). */
  available: boolean;
  artifacts: MediaArtifact[];
  /** Exact media-kind count from the server (first-page reads), for the
   *  Media folder tile. Undefined when the server omitted it. */
  mediaTotal?: number;
}

function mediaKey(userId: string | null, limit: number): readonly unknown[] {
  return ["media-artifacts", userId ?? "anon", limit] as const;
}

async function fetchArtifacts(
  token: string | null,
  limit: number
): Promise<ArtifactsResult> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await resilientFetch(
    `${SERVER_URL}/api/media/artifacts?limit=${limit}`,
    { headers }
  );
  if (res.status === 404) return { available: false, artifacts: [] };
  if (!res.ok) throw new Error(`media artifacts request failed: ${res.status}`);
  const body = (await res.json()) as MediaArtifactsResponse;
  return {
    available: true,
    artifacts: body.artifacts ?? [],
    mediaTotal: body.media_total,
  };
}

export interface UseMediaArtifacts {
  artifacts: MediaArtifact[];
  available: boolean;
  loading: boolean;
  error: unknown;
  /** Exact media-kind count for the Media folder tile (undefined until
   *  the first page lands, or when the content engine is off). */
  mediaTotal?: number;
}

/** The full Media Library listing (the grid). */
export function useMediaArtifacts(limit = 48): UseMediaArtifacts {
  const { user, getAccessToken } = useAuth();
  const userId = user?.id ?? null;
  const query = useQuery<ArtifactsResult>({
    queryKey: mediaKey(userId, limit),
    queryFn: async () => fetchArtifacts(await getAccessToken(), limit),
    enabled: !!userId,
    staleTime: 30_000,
  });
  return {
    artifacts: query.data?.artifacts ?? [],
    available: query.data?.available ?? false,
    loading: query.isPending && !!userId,
    error: query.error,
    mediaTotal: query.data?.mediaTotal,
  };
}

export type MediaLibraryAvailability = "loading" | "on" | "off";

/**
 * Tab-visibility probe for the Knowledge layout. Reads the SAME query the grid
 * uses (default limit) so the check and the grid share one fetch, rather than
 * forking a second cache entry with a different limit. Tri-state: 'loading' until
 * the probe resolves, then 'on' (200) or 'off' (404 — content engine flag off).
 * The tri-state lets the layout keep the Media deep-link lit while loading.
 */
export function useMediaLibraryAvailability(): MediaLibraryAvailability {
  const { available, loading } = useMediaArtifacts();
  if (loading) return "loading";
  return available ? "on" : "off";
}
